import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { getArchives } from '@/lib/api'
import type { Archive } from '@/types'
import { SmartImage } from './SmartImage'

// Presentation only. The archive LIST/ORDER comes from the API (getArchives);
// this map holds nothing but per-archive styling + the custom short label.
// Keys not present here fall back to the gold accent + a name-derived label,
// so a future archive always renders a usable chip instead of breaking.
type ChipStyle = { color: string; border: string; label: string }

const STYLE_MAP: Record<string, ChipStyle> = {
  treaty: { color: 'from-blue-500/20 to-blue-600/10', border: 'border-blue-500/30 hover:border-blue-400/60', label: 'Treaty' },
  conflict: { color: 'from-red-500/20 to-red-600/10', border: 'border-red-500/30 hover:border-red-400/60', label: 'Conflict' },
  civilization: { color: 'from-amber-500/20 to-amber-600/10', border: 'border-amber-500/30 hover:border-amber-400/60', label: 'Civilization' },
  origin: { color: 'from-emerald-500/20 to-emerald-600/10', border: 'border-emerald-500/30 hover:border-emerald-400/60', label: 'Companies' },
  lineage: { color: 'from-purple-500/20 to-purple-600/10', border: 'border-purple-500/30 hover:border-purple-400/60', label: 'Lineage' },
  classified: { color: 'from-slate-500/20 to-slate-600/10', border: 'border-slate-500/30 hover:border-slate-400/60', label: 'Classified' },
  exploration: { color: 'from-cyan-500/20 to-cyan-600/10', border: 'border-cyan-500/30 hover:border-cyan-400/60', label: 'Exploration' },
  mythology: { color: 'from-indigo-500/20 to-indigo-600/10', border: 'border-indigo-500/30 hover:border-indigo-400/60', label: 'Mythology' },
  fraud: { color: 'from-[#8FB89A]/20 to-[#8FB89A]/10', border: 'border-[#8FB89A]/30 hover:border-[#8FB89A]/60', label: 'Fraud' },
  creed: { color: 'from-[#C8A24B]/20 to-[#C8A24B]/10', border: 'border-[#C8A24B]/30 hover:border-[#C8A24B]/60', label: 'Creed' },
  disaster: { color: 'from-orange-500/20 to-orange-600/10', border: 'border-orange-500/30 hover:border-orange-400/60', label: 'Disaster' },
  philosophy: { color: 'from-teal-500/20 to-teal-600/10', border: 'border-teal-500/30 hover:border-teal-400/60', label: 'Philosophy' },
}

// Curated display order for known archives (preserves the original 10's exact
// positions; disaster + philosophy follow). Any API archive not listed here is
// appended after these, in API order.
const CURATED_ORDER = Object.keys(STYLE_MAP)

const FALLBACK_COLOR = 'from-archive-gold/20 to-archive-gold/10'
const FALLBACK_BORDER = 'border-archive-gold/30 hover:border-archive-gold/60'

// "The Origin Archive" -> "Origin" (only used when a key isn't in STYLE_MAP)
const deriveLabel = (name: string) =>
  name.replace(/^The\s+/i, '').replace(/\s+Archive$/i, '').trim()

// Static seed so the above-the-fold grid never flashes empty or collapses on a
// slow/failed fetch. Replaced by the API list once it resolves.
const FALLBACK_ARCHIVES: Pick<Archive, 'key' | 'name'>[] = CURATED_ORDER.map((key) => ({
  key,
  name: STYLE_MAP[key].label,
}))

export default function HeroRotunda() {
  const [archives, setArchives] = useState<Pick<Archive, 'key' | 'name'>[]>(FALLBACK_ARCHIVES)

  useEffect(() => {
    getArchives()
      .then((list) => {
        if (list && list.length) setArchives(list)
      })
      .catch(() => {
        /* keep FALLBACK_ARCHIVES — never collapse the hero */
      })
  }, [])

  // Known keys first (curated order), then future archives the map doesn't know.
  const known = CURATED_ORDER.map((key) => archives.find((a) => a.key === key)).filter(
    (a): a is Pick<Archive, 'key' | 'name'> => Boolean(a),
  )
  const extras = archives.filter((a) => !STYLE_MAP[a.key])
  const orderedArchives = [...known, ...extras]

  return (
    <section className="relative overflow-hidden px-4 py-16 sm:px-6 sm:py-24 lg:py-32">
      {/* Background gradient effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-archive-charcoal/80 via-archive-black to-archive-charcoal/50" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(201,169,98,0.08)_0%,transparent_60%)]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[radial-gradient(circle,rgba(201,169,98,0.03)_0%,transparent_70%)]" />

      {/* Circular decorative pattern - matching archive sites */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='260' height='260' viewBox='0 0 260 260' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23C9A962' stroke-width='1'%3E%3Cpath d='M0 130h260M130 0v260'/%3E%3Ccircle cx='130' cy='130' r='70'/%3E%3Ccircle cx='130' cy='130' r='110'/%3E%3C/g%3E%3C/svg%3E")`,
          backgroundPosition: 'center',
        }}
      />

      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A962' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }} />

      <div className="relative mx-auto max-w-7xl">
        <div className="flex flex-col items-center">
          {/* Premium badge */}
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-archive-gold/30 bg-archive-gold/10 px-4 py-2 backdrop-blur-sm">
            <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-archive-gold" />
            <p className="text-[10px] uppercase tracking-[0.4em] text-archive-gold sm:text-xs">Premium Archives</p>
          </div>

          {/* Central seal/logo */}
          <div className="relative mb-10">
            {/* Outer glow rings */}
            <div className="absolute -inset-8 rounded-full border border-archive-gold/5" />
            <div className="absolute -inset-16 rounded-full border border-archive-gold/[0.03]" />
            <div className="absolute -inset-24 rounded-full border border-archive-gold/[0.02] hidden sm:block" />

            {/* Glow effect */}
            <div className="absolute -inset-4 rounded-full bg-archive-gold/10 blur-2xl" />
            <div className="absolute -inset-8 rounded-full bg-archive-gold/5 blur-3xl" />

            {/* Logo container */}
            <div className="relative w-40 h-40 sm:w-52 sm:h-52 lg:w-64 lg:h-64 rounded-full overflow-hidden">
              <SmartImage
                src="/images/archive-network-seal.png"
                alt="The Archive Network"
                sizes="(min-width: 1024px) 256px, 160px"
                width={256}
                height={256}
                loading="eager"
                className="w-full h-full object-contain drop-shadow-2xl"
              />
            </div>
          </div>

          {/* Headline */}
          <h1 className="text-center font-display text-3xl font-semibold leading-tight text-archive-cream sm:text-4xl md:text-5xl lg:text-6xl">
            The unified gateway to{' '}
            <span className="bg-gradient-to-r from-archive-gold via-archive-amber to-archive-gold bg-clip-text text-transparent">
              humanity's memory
            </span>
          </h1>

          {/* Subhead */}
          <p className="mt-6 max-w-2xl text-center text-base leading-relaxed text-archive-muted sm:text-lg lg:text-xl">
            A growing network of interconnected archives, each documenting a distinct domain of human history — explored through podcasts, documentaries, and curated artifacts.
          </p>

          {/* Search link styled as search bar */}
          <Link
            to="/search"
            className="mt-10 w-full max-w-2xl flex items-center gap-3 rounded-full border border-archive-gold/30 bg-archive-black/60 px-6 py-4 text-archive-muted backdrop-blur-sm transition-all duration-300 hover:border-archive-gold/60 hover:bg-archive-black/80"
          >
            <svg className="h-5 w-5 text-archive-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span>Search across all archives...</span>
          </Link>

          {/* Archive links - responsive grid, list sourced from the API */}
          <div className="mt-12 w-full max-w-4xl">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
              {orderedArchives.map((archive) => {
                const style = STYLE_MAP[archive.key]
                const color = style?.color ?? FALLBACK_COLOR
                const border = style?.border ?? FALLBACK_BORDER
                const label = style?.label ?? deriveLabel(archive.name)
                return (
                  <Link
                    key={archive.key}
                    to={`/archive/${archive.key}`}
                    className={`group relative overflow-hidden rounded-2xl border bg-gradient-to-br ${color} ${border} p-4 text-center transition-all duration-300 hover:scale-[1.02] sm:p-5`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-archive-black/50 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <h3 className="relative font-display text-sm font-semibold text-archive-cream transition-colors group-hover:text-white sm:text-base">
                      {label}
                    </h3>
                    <p className="relative mt-1 text-[10px] uppercase tracking-wider text-archive-muted sm:text-xs">
                      Archive
                    </p>
                  </Link>
                )
              })}
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:gap-6">
            <Link
              to="/explore"
              className="group relative overflow-hidden rounded-full bg-gradient-to-r from-archive-gold to-archive-amber px-8 py-4 text-sm font-semibold uppercase tracking-wider text-archive-black shadow-lg shadow-archive-gold/20 transition-all duration-300 hover:shadow-xl hover:shadow-archive-gold/30"
            >
              <span className="relative z-10">Start Exploring</span>
              <div className="absolute inset-0 bg-gradient-to-r from-archive-amber to-archive-gold opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </Link>
            <Link
              to="/podcasts"
              className="rounded-full border border-archive-gold/50 px-8 py-4 text-sm font-semibold uppercase tracking-wider text-archive-gold transition-all duration-300 hover:bg-archive-gold/10 hover:border-archive-gold"
            >
              Listen to Podcasts
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom gradient line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-archive-gold/30 to-transparent" />
    </section>
  )
}
